import { Show, createMemo, createSignal } from "solid-js";
import { persistantStore } from "@store/persistantStore";
import { isLoggedIn } from "@store/userStore";
import { isProjectUnderMaintenance } from "@utils/projectStatus";

const { selectedProjectId, projectStatus, maintenanceMessage } = persistantStore;

const DISMISS_KEY_PREFIX = "maintenanceBannerDismissed:";

/**
 * Under-maintenance notice for the project the user is on.
 *
 * Rendered as a `position: fixed` overlay, NOT an in-flow strip: Styles.css has ~80 hard-coded
 * `calc(100vh - 60px)` rules keyed to the 60px header, so anything that occupies layout space here
 * pushes every scaled page down and breaks its fit-to-height maths.
 *
 * Dismissal is per project + per message text (sessionStorage), so acknowledging one notice does
 * not hide the next one. The header badge stays visible after dismissal.
 */
export default function MaintenanceBanner() {
  const [dismissedKey, setDismissedKey] = createSignal<string | null>(null);

  const currentKey = createMemo(() => `${DISMISS_KEY_PREFIX}${selectedProjectId()}|${maintenanceMessage()}`);

  const isDismissed = createMemo(() => {
    const key = currentKey();
    if (dismissedKey() === key) {
      return true;
    }
    try {
      return sessionStorage.getItem(key) === "1";
    } catch {
      // Private mode / storage disabled — just show the banner.
      return false;
    }
  });

  const dismiss = () => {
    const key = currentKey();
    try {
      sessionStorage.setItem(key, "1");
    } catch {
      // Ignore: the signal below still hides it for this page view.
    }
    setDismissedKey(key);
  };

  return (
    <Show when={isLoggedIn() && selectedProjectId() > 0 && isProjectUnderMaintenance(projectStatus()) && !isDismissed()}>
      <div class="maintenance-banner" role="status" aria-live="polite">
        <svg
          class="maintenance-banner-icon"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M12 9V13M12 17H12.01M10.29 3.86L1.82 18A2 2 0 0 0 3.55 21H20.45A2 2 0 0 0 22.18 18L13.71 3.86A2 2 0 0 0 10.29 3.86Z"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span class="maintenance-banner-label">Under maintenance</span>
        <span class="maintenance-banner-message">{maintenanceMessage()}</span>
        <button
          type="button"
          class="maintenance-banner-dismiss"
          aria-label="Dismiss maintenance notice"
          title="Dismiss"
          onClick={dismiss}
        >
          &times;
        </button>
      </div>
    </Show>
  );
}
